/**
 * The file-preview allowlist in force for each project cwd.
 *
 * `ChatMarkdown` needs this to decide whether a file path mentioned in a
 * message is worth offering as a link, and it renders once per message in
 * many different contexts — including places with no react-query provider
 * around them. So this follows `projectConfigStore`'s shape (a module
 * singleton keyed by cwd, deduping its own in-flight loads) rather than the
 * `…ReactQuery.ts` convention, for the same reason that store exists: a leaf
 * component should not drag a provider requirement in with it.
 *
 * Missing entry means "not loaded yet", which reads the same as "nothing
 * configured" — chat offers no links until the real allowlist arrives, so a
 * slow or failed load can never make a path clickable that shouldn't be.
 */
import { create } from "zustand";
import { readNativeApi } from "./nativeApi";

type FilePreviewGlobsStatus = "loading" | "loaded" | "error";

interface FilePreviewGlobsEntry {
  readonly status: FilePreviewGlobsStatus;
  readonly allowedGlobs: readonly string[];
}

interface FilePreviewGlobsStoreState {
  readonly byCwd: Record<string, FilePreviewGlobsEntry>;
  readonly load: (cwd: string) => Promise<void>;
}

/** Shared empty result, so a component reading "nothing configured" keeps a
 * stable reference across renders instead of re-deriving a new array. */
export const NO_ALLOWED_GLOBS: readonly string[] = [];

export const useFilePreviewGlobsStore = create<FilePreviewGlobsStoreState>()((set, get) => ({
  byCwd: {},
  load: async (cwd) => {
    if (get().byCwd[cwd]) return;

    set((state) => ({
      byCwd: { ...state.byCwd, [cwd]: { status: "loading", allowedGlobs: NO_ALLOWED_GLOBS } },
    }));

    const api = readNativeApi();
    if (!api) {
      set((state) => ({
        byCwd: { ...state.byCwd, [cwd]: { status: "error", allowedGlobs: NO_ALLOWED_GLOBS } },
      }));
      return;
    }

    try {
      const result = await api.files.previewGlobs({ cwd });
      set((state) => ({
        byCwd: { ...state.byCwd, [cwd]: { status: "loaded", allowedGlobs: result.allowedGlobs } },
      }));
    } catch {
      // Leaving this as "error" with an empty allowlist means chat simply
      // offers no file links for this project — the same as unconfigured.
      set((state) => ({
        byCwd: { ...state.byCwd, [cwd]: { status: "error", allowedGlobs: NO_ALLOWED_GLOBS } },
      }));
    }
  },
}));

export function selectAllowedGlobs(
  state: FilePreviewGlobsStoreState,
  cwd: string | undefined,
): readonly string[] {
  if (!cwd) return NO_ALLOWED_GLOBS;
  return state.byCwd[cwd]?.allowedGlobs ?? NO_ALLOWED_GLOBS;
}
